import {
  Component,
  OnDestroy,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { initializeApp } from 'firebase/app';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { BackgroundFetch } from '@transistorsoft/capacitor-background-fetch';
import { Network } from '@capacitor/network';
import { IonToast, ModalController, ToastController } from '@ionic/angular';
import { NavigationEnd, Router } from '@angular/router';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  fromEvent,
  merge,
  of,
  Subject,
  switchMap,
  takeUntil
} from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../environments/environment';
import {
  AppModeStatus,
  OfflineModeService
} from './services/offline-mode.service';
import { ErrorToastService } from './services/error-toast.service';
import {
  BackupService,
  ConflictVersionModalData
} from './services/backup.service';
import { VersionConflictModalComponent } from './components/version-conflict-modal/version-conflict-modal.component';
import { DocumentsSubmitService } from './services/documents-submit.service';
import { DbCleanupService } from './services/db-cleanup.service';
import { BackgroundSyncService } from './services/sync/background-sync.service';
import * as Sentry from '@sentry/angular';
import { HealthCheckService } from './services/healthcheck/health-check.service';
import { HmsCoreCheckService } from './services/hms-core-check.service';

interface NotificationData {
  requestId?: string;
}

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss']
})
export class AppComponent implements OnDestroy {
  @ViewChild('errorToast') errorToast: IonToast;
  @ViewChild('bankingDataError') bankingDataError: IonToast;
  @ViewChild('modalContainer', { read: ViewContainerRef })
  modalContainer: ViewContainerRef;

  backupModalData: ConflictVersionModalData;

  // Открытая модалка конфликта версий и синхронный флаг её открытия. Защита от наложения: при
  // неразрешённом конфликте фоновая синхронизация повторяет отправку бэкапа и повторно порождает
  // событие конфликта на каждом цикле, поэтому событий приходит много — а модалка конфликта должна
  // оставаться одна. Подавлённый конфликт другого заказа сам вернётся следующим циклом после
  // разрешения текущего.
  private conflictModal: HTMLIonModalElement | null = null;
  private isOpeningConflictModal = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    protected offlineService: OfflineModeService,
    private errorToastService: ErrorToastService,
    private toastController: ToastController,
    private backupService: BackupService,
    private modalCtrl: ModalController,
    private router: Router,
    private readonly documentsSubmitService: DocumentsSubmitService,
    private readonly dbCleanupService: DbCleanupService,
    private readonly backgroundSyncService: BackgroundSyncService,
    private readonly healthCheckService: HealthCheckService,
    private readonly hmsCoreCheckService: HmsCoreCheckService
  ) {
    this.initVersionConflictListener();
    this.initErrorToastListener();
    this.initNetworkListener();
    this.initPushListeners();
    this.initDocumentsSubmitService();
    void this.initializeFirebase();
    if (Capacitor.isNativePlatform()) {
      void this.initBackgroundFetch();
    }
    void this.requestPermissions();
    void this.dbCleanupService.cleanupDbData();
    void this.initHealthCheck();
    void this.hmsCoreCheckService.checkAndPromptHmsCore();
  }

  async showNotificationToastText(header: string, text: string) {
    try {
      const toast = await this.toastController.create({
        message: text,
        header,
        duration: 4000,
        cssClass: 'notification-toast',
        icon: 'alert-circle-outline'
      });

      await toast.present();
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  public async initializeFirebase(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      return;
    }
    initializeApp(environment.firebase);
  }

  public async requestPermissions(): Promise<void> {
    await FirebaseMessaging.requestPermissions();
  }

  async initBackgroundFetch() {
    const status = await BackgroundFetch.configure(
      {
        minimumFetchInterval: 15
      },
      async taskId => {
        await this.performYourWorkHere();
        BackgroundFetch.finish(taskId);
      },
      async taskId => {
        BackgroundFetch.finish(taskId);
      }
    );

    // Checking BackgroundFetch status:
    if (status !== BackgroundFetch.STATUS_AVAILABLE) {
      // Uh-oh:  we have a problem:
      if (status === BackgroundFetch.STATUS_DENIED) {
        alert(
          'The user explicitly disabled background behavior for this app or for the whole system.'
        );
      } else if (status === BackgroundFetch.STATUS_RESTRICTED) {
        alert(
          'Background updates are unavailable and the user cannot enable them again.'
        );
      }
    }
  }

  async performYourWorkHere() {
    this.backgroundSyncService.runAllActions();
    await firstValueFrom(this.documentsSubmitService.submitPendingDocuments());
  }

  async presentErrorToast() {
    try {
      if (this.errorToast) {
        await this.errorToast.present();
      }
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  async createModal(data: ConflictVersionModalData) {
    // Пока предыдущая модалка конфликта на экране (или в процессе открытия) — новые события конфликта
    // игнорируем: тот же неразрешённый конфликт повторно порождается каждым циклом синхронизации, и без
    // этой защиты модалки наложились бы стопкой. Синхронный флаг закрывает окно гонки между проверкой и
    // присваиванием ссылки (между ними — await modalCtrl.create).
    if (this.conflictModal || this.isOpeningConflictModal) {
      return;
    }
    this.isOpeningConflictModal = true;
    try {
      const id = `modal-${Math.random().toString(36).substr(2, 9)}`;
      const modal = await this.modalCtrl.create({
        component: VersionConflictModalComponent,
        breakpoints: [0, 1],
        initialBreakpoint: 1,
        componentProps: {
          data,
          id
        },
        backdropDismiss: false,
        keyboardClose: false
      });
      this.conflictModal = modal;
      void modal.onDidDismiss().then(() => {
        this.conflictModal = null;
      });
      await modal.present();
    } catch (e) {
      // Открытие сорвалось — снимаем ссылку, чтобы защита не «залипла» и следующий конфликт мог открыться.
      this.conflictModal = null;
      Sentry.captureException(e);
    } finally {
      this.isOpeningConflictModal = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async initHealthCheck(): Promise<void> {
    this.healthCheckService
      .checkOnInit()
      .pipe(takeUntil(this.destroy$))
      .subscribe(success => {
        if (!success) {
          Sentry.captureMessage('Initial health check failed', {
            level: 'error',
            tags: {
              component: 'AppComponent',
              method: 'initHealthCheck'
            }
          });
        }
      });
  }

  private initDocumentsSubmitService(): void {
    merge(
      fromEvent(document, 'resume'),
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        debounceTime(5000)
      ),
      this.offlineService.appModeStatus$.pipe(
        distinctUntilChanged(),
        filter(status => status === AppModeStatus.ONLINE)
      )
    )
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() =>
          this.documentsSubmitService
            .submitPendingDocuments()
            .pipe(catchError(() => of(null)))
        )
      )
      .subscribe();
  }

  private initVersionConflictListener(): void {
    this.backupService.versionConflictModalTrigger$
      .pipe(takeUntil(this.destroy$))
      .subscribe(r => {
        if (r) {
          this.backupModalData = r;
          this.createModal(r);
        }
      });
  }

  private initErrorToastListener(): void {
    this.errorToastService.errorSubject
      .pipe(takeUntil(this.destroy$))
      .subscribe(r => {
        switch (r) {
          case 424:
            if (this.bankingDataError) {
              void this.bankingDataError.present();
            }
            break;
          case 500:
            void this.presentErrorToast();
            break;
          default:
            break;
        }
      });
  }

  private initNetworkListener(): void {
    void Network.addListener('networkStatusChange', status => {
      if (status.connected) {
        this.offlineService.appModeStatus$.next(AppModeStatus.ONLINE);
        this.backgroundSyncService.runAllActions();
        void this.backupService.executeBackupActions();
        void this.backupService.checkUpdates();
      } else {
        this.offlineService.appModeStatus$.next(AppModeStatus.OFFLINE);
      }
    });
  }

  private initPushListeners(): void {
    void FirebaseMessaging.addListener('notificationReceived', event => {
      if (Capacitor.getPlatform() === 'android') {
        void this.showNotificationToastText(
          `${event.notification.title} `,
          `${event.notification.body}`
        );
      }
    });

    void FirebaseMessaging.addListener(
      'notificationActionPerformed',
      event => {
        if (event.actionId === 'tap') {
          const { requestId } = event.notification.data as NotificationData;
          void this.router.navigate([
            requestId ? `/requests/days/${requestId}` : '/requests'
          ]);
        }
      }
    );
  }
}
